import { useEffect } from "react";
import { Link } from "react-router-dom";
import { DoorOpen, Home, TrendingDown, MessageSquare, CalendarClock, Zap, CalendarCheck, HeartHandshake, CheckCircle, MessageSquare as SmsIcon, Smartphone, Megaphone, PhoneCall } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const useCases = [
  {
    title: "Open House Invitations",
    description: "Send personalized invitations for open house sessions to boost attendance.",
    Icon: DoorOpen,
    color: "text-amber",
    tags: ["SMS"],
  },
  {
    title: "New Listing Promotions",
    description: "Let buyers know when a new property hits the market before it's seen by everyone.",
    Icon: Home,
    color: "text-sky",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Price Drop Notifications",
    description: "Alert potential buyers about price drops to reignite interest in previously viewed properties.",
    Icon: TrendingDown,
    color: "text-lime",
    tags: ["SMS"],
  },
  {
    title: "Follow-Ups With Leads",
    description: "Check in with old leads, share property updates, and keep the deal on their radar.",
    Icon: MessageSquare,
    color: "text-violet",
    tags: ["SMS"],
  },
  {
    title: "House Tour Reminders",
    description: "Send scheduled reminders about property tours and meetings to prevent no-shows.",
    Icon: CalendarClock,
    color: "text-pink",
    tags: ["SMS", "RCS"],
  },
];

const enhancedBenefits = [
  {
    title: "Faster Lead Response",
    description: "Instant alerts help agents reach new leads while interest is still high.",
    Icon: Zap,
    color: "text-amber",
  },
  {
    title: "Fewer Missed Viewings",
    description: "Automated reminders reduce no-shows and keep schedules running smoothly.",
    Icon: CalendarCheck,
    color: "text-sky",
  },
  {
    title: "Stronger Client Follow-Up",
    description: "Consistent, personalized messaging keeps agents top-of-mind with buyers and sellers.",
    Icon: HeartHandshake,
    color: "text-lime",
  },
];

const supportedChannels = [
  { title: "SMS", description: "Reach agents and clients instantly for lead alerts and updates.", Icon: SmsIcon, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Deliver interactive appointment and listing updates.", Icon: Megaphone, gradient: "icon-tile-indigo" },
  { title: "Voice", description: "Reach clients quickly with automated voice reminders and follow-ups.", Icon: PhoneCall, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "What is SMS messaging used for in real estate?", answer: "Real estate teams use SMS to send listing updates, appointment reminders, price changes, and lead follow-ups, helping agents stay top-of-mind and respond to buyers and sellers in real time." },
  { question: "Is SMS marketing for real estate legal?", answer: "Yes, when done in compliance with regulations like the TCPA and GDPR. Agencies must obtain prior consent before sending promotional messages, include opt-out options, and respect recipient privacy." },
  { question: "Can I send unsolicited texts to potential leads?", answer: "No. Sending messages without the recipient's consent (cold texting) is not permitted in most jurisdictions. Explicit consent is required before sending promotional real estate messages." },
  { question: "Can I automate follow-ups with leads?", answer: "Yes, Yootelco supports automated messaging flows for lead follow-up, listing alerts, and appointment reminders." },
  { question: "Can this reduce missed viewings?", answer: "Yes, automated tour and appointment reminders are one of the most effective ways to reduce no-shows." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const channelIcons = [
  { label: "SMS", Icon: SmsIcon },
  { label: "RCS", Icon: Megaphone },
];

const IndustriesRealEstate = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="Real Estate SMS: Lead & Client Messaging | Yootelco"
        description="Personalize your outreach and close more deals with SMS marketing for real estate. Send listing alerts, reminders, and follow-ups via SMS and RCS."
        path="/industries/real-estate"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Stay Top-of-Mind With Every Lead
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Messaging for Real Estate
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Personalize your outreach and close more deals with SMS messaging built for real estate agencies. Yootelco helps agents send listing alerts, reminders, and follow-ups that keep deals moving.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {channelIcons.map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <channel.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {channel.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Real Estate Teams Choose Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Why Real Estate Teams Choose Yootelco</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            With more than 18 years of experience in business communications, we help real estate agencies, property developers, and proptech companies connect with buyers, tenants, and investors through fast, reliable, and personalized communication solutions. Whether you're promoting new developments, qualifying leads, scheduling property viewings, or keeping clients informed throughout the sales process, our solutions are designed to improve engagement and accelerate conversions.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            From lead generation and customer support to appointment reminders, marketing campaigns, and automated client communications, we help real estate businesses streamline every stage of the customer journey.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Trending Use Cases in Real Estate</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} className="mx-auto block w-fit" />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Enhanced Benefits */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Enhanced Benefits for Real Estate</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {enhancedBenefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Automated messaging flows for every stage of a deal",
              "Multi-channel delivery across SMS and RCS",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Talk to Sales"
        description="Tell us about your agency's lead volume, we'll show you how Yootelco can help you respond faster and follow up automatically."
        useCase="Real Estate"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Ready to Respond to Leads Faster?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            Talk to our team about lead notifications and client messaging for your agency.
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesRealEstate;
